// Single source of truth for the "is the agent's profile complete" checks and the
// routes/URLs used to fix each one - the red action buttons on the Home screen
// (views/Home/index.jsx) and the LGPromptXWidget's "Help me complete my profile"
// flow both read from here, so changing a route or a condition only has to happen
// in one place to take effect everywhere.
import { isAnyAgentLogoAdded, isAnyAgentPhotoAdded } from 'common/helpers/AgentData';
import { isBrokerageDetailsIncomplete } from 'views/AgentDetails/utils/brokerage';

// Plain routes (no agent ID needed in the path).
export const PROFILE_ROUTES = {
  CLIENT_TESTIMONIALS: '/agent/clienttestimonials',
  MISSION_STATEMENTS: '/agent/missionstatements',
};

// AgentDetails is one page with different #anchors per section - callers pass
// whichever already-resolved agent ID they have (Home and the widget currently
// each source it differently), so this stays agnostic of where the ID comes from.
export const getAgentDetailsUrl = (agentId, anchor = '') =>
  `/agent/details/ag/${agentId ? btoa(agentId) : ''}${anchor}`;

export const isAnyAreaTaglineAdded = (navbarInfo = []) =>
  Array.isArray(navbarInfo) && navbarInfo.some((area) => area && area.tagLine && area.tagLine.trim());

export const isAnyTestimonialAdded = (agentData = {}) =>
  !!(agentData.testimonialsCount && agentData.testimonialsCount > 0);

// Driven by getAgentProfile's IsMissionStatementSelected flag, which is set only once a
// valid statement is selected via setCurrentMissionStatement.
export const isMissionStatementSelected = (agentData = {}) =>
  !!(agentData.isMissionStatementSelected ?? agentData.IsMissionStatementSelected);

export { isAnyAgentLogoAdded, isAnyAgentPhotoAdded, isBrokerageDetailsIncomplete };

// The 6 items, in the same order as Home's own red buttons - only the incomplete
// ones. Consumers that need individual booleans (e.g. Home, for per-button
// className logic) should use the check functions above directly instead.
export const getIncompleteProfileItems = (agentData = {}, agentId) => {
  const items = [];

  if (!isAnyAgentPhotoAdded(agentData)) {
    items.push({ key: 'photo', label: 'Your Photo', url: getAgentDetailsUrl(agentId, '#agent-photo-section') });
  }
  if (!isAnyAgentLogoAdded(agentData)) {
    items.push({ key: 'logo', label: 'Your Logo', url: getAgentDetailsUrl(agentId, '#agent-logo-section') });
  }
  if (!isAnyAreaTaglineAdded(agentData.navbarInfo)) {
    items.push({ key: 'areaTaglines', label: 'Area Taglines', url: getAgentDetailsUrl(agentId, '#area-tagline-section') });
  }
  if (!isAnyTestimonialAdded(agentData)) {
    items.push({ key: 'testimonials', label: 'Client Testimonials', url: PROFILE_ROUTES.CLIENT_TESTIMONIALS });
  }
  if (!isMissionStatementSelected(agentData)) {
    items.push({ key: 'missionStatement', label: 'Your Mission Statement', url: PROFILE_ROUTES.MISSION_STATEMENTS });
  }
  if (isBrokerageDetailsIncomplete(agentData)) {
    items.push({ key: 'brokerage', label: 'Brokerage Office Location', url: getAgentDetailsUrl(agentId, '#brokerage-office-section') });
  }

  return items;
};

// Same 3 fields buildIncompleteProfileItems' readiness gate already checks -
// centralized here so both callers agree on what "still loading" looks like.
export const isProfileCompletenessDataReady = (agentData = {}) =>
  Array.isArray(agentData.navbarInfo) &&
  agentData.testimonialsCount !== undefined &&
  agentData.missionStatementsCount !== undefined;
